/**
 * Ballistics - resolucao de tiro (secao 4.12).
 *
 * E o unico lugar do jogo que decide "esta bala acertou o que?". Vale para o
 * jogador E para os bots: quem atira chama fire(...) com o proprio time, e a
 * balistica cuida de dispersao, penetracao, dano, eventos e FX.
 *
 * Decisoes que nao sao obvias, e o porque:
 *
 * 1. CENARIO E ALVO SAO CONSULTADOS JUNTOS, e vence o mais proximo. Testar o
 *    inimigo antes e "se acertou, acabou" e o bug classico do genero: a bala
 *    atravessa a parede e mata quem esta atras. Aqui os dois raios sao lancados
 *    com o mesmo alcance e comparados por distancia.
 *
 * 2. DISPERSAO EM CONE COM DENSIDADE NO CENTRO. Sorteio uniforme por AREA
 *    (raio = R*sqrt(u)) espalha demais e faz a arma parecer quebrada. Usamos
 *    raio = R*u^0.7, que concentra a maioria dos tiros perto da mira e ainda
 *    permite o tiro perdido ocasional. Para cartucho de espingarda a
 *    distribuicao e estratificada em espiral (angulo aureo) com ruido: o
 *    padrao fica cheio e coerente em vez de embolado ao acaso.
 *
 * 3. PENETRACAO com no maximo 2 superficies por tiro. So madeira, metal fino,
 *    saco de areia e vidro deixam passar; concreto, tijolo e metal grosso param.
 *    Cada travessia custa dano e desvia levemente a trajetoria.
 *
 * 4. TUDO NO MESMO FRAME. Dano, hitmarker, impacto e tracer saem de dentro de
 *    fire(), sincronos. Nada e enfileirado para o proximo frame - atraso de um
 *    frame entre o tiro e o retorno visual/sonoro e sentido mesmo por quem nao
 *    sabe explicar o que sentiu.
 *
 * 5. ZERO ALOCACAO POR TIRO. Vetores, payloads de evento e argumentos de FX sao
 *    temporarios de modulo, reaproveitados. Consequencia para quem escuta:
 *    OS PAYLOADS SAO REUTILIZADOS - copie o que precisar (point, normal) na
 *    hora; nao guarde a referencia para o proximo frame.
 */

import * as THREE from 'three';
import { HITBOX_MULTIPLIER, TEAM } from '../core/Constants.js';
import { EVENTS } from '../core/EventBus.js';

const TAU = Math.PI * 2;
const DEG2RAD = Math.PI / 180;
const GOLDEN_ANGLE = 2.39996323;

const DEFAULT_RANGE = 200;
const DEFAULT_DAMAGE = 24;
const MAX_PELLETS = 24;
const MAX_PENETRATIONS = 2;

/** Avanco alem da face atingida para o raio seguinte nao reacertar a mesma face. */
const PENETRATION_STEP = 0.02;
/** Acima disto nao e mais "a saida da mesma parede", e outra superficie. */
const WALL_MAX_THICKNESS = 0.6;
/** Empate a favor do alvo: bot encostado na parede continua atingivel. */
const HIT_EPS = 1e-4;

/**
 * Superficies que a bala atravessa. Lista fechada (as demais param o tiro).
 *   damage    - fator de dano que sobra depois de atravessar
 *   deviation - desvio maximo da trajetoria, em graus
 */
const PENETRABLE = {
  glass: { damage: 0.92, deviation: 0.35 },
  wood: { damage: 0.70, deviation: 0.90 },
  metal_thin: { damage: 0.58, deviation: 1.10 },
  sandbag: { damage: 0.42, deviation: 1.40 }
};

/** Curva padrao de queda de dano quando a arma nao declara a dela. */
const FALLOFF_START_FRACTION = 0.35;
const FALLOFF_END_FRACTION = 0.95;
const FALLOFF_MIN = 0.5;

// --- temporarios de modulo (zero alocacao no caminho quente) -----------------
const _origin = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3();
const _pelletDir = new THREE.Vector3();
const _curOrigin = new THREE.Vector3();
const _curDir = new THREE.Vector3();
const _endPoint = new THREE.Vector3();

const _tPoint = new THREE.Vector3();
const _tNormal = new THREE.Vector3();
const _cPoint = new THREE.Vector3();
const _cNormal = new THREE.Vector3();

const _tracerFrom = new THREE.Vector3();
const _muzzleTmp = new THREE.Vector3();

const _WORLD_UP = new THREE.Vector3(0, 1, 0);
const _AXIS_X = new THREE.Vector3(1, 0, 0);

// --- payloads reaproveitados -------------------------------------------------
const _evImpact = {
  point: new THREE.Vector3(),
  normal: new THREE.Vector3(),
  surface: 'concrete',
  object: null,
  fromPlayer: false,
  weapon: null,
  distance: 0,
  penetrated: false
};

const _evDamaged = {
  entity: null,
  amount: 0,
  part: 'chest',
  point: new THREE.Vector3(),
  normal: new THREE.Vector3(),
  source: null,
  weapon: null,
  headshot: false,
  distance: 0,
  killed: false
};

const _evKilled = { entity: null, source: null, weapon: null, headshot: false, part: 'chest' };
const _evHitmarker = { headshot: false, killed: false };

const _dmgInfo = {
  amount: 0,
  part: 'chest',
  point: new THREE.Vector3(),
  normal: new THREE.Vector3(),
  direction: new THREE.Vector3(),
  from: new THREE.Vector3(),
  source: null,
  weapon: null,
  team: -1,
  distance: 0,
  headshot: false
};

const _fxImpactArgs = { point: new THREE.Vector3(), normal: new THREE.Vector3(), surface: 'concrete', scale: 1 };
const _fxBloodArgs = { point: new THREE.Vector3(), normal: new THREE.Vector3(), direction: new THREE.Vector3() };
const _fxTracerArgs = { from: new THREE.Vector3(), to: new THREE.Vector3(), speed: 340, weapon: null, fromPlayer: false };

/** Resumo do ultimo tiro. REAPROVEITADO - leia na hora. */
const _shotResult = { pellets: 0, hits: 0, kills: 0, damage: 0, penetrations: 0 };

function finite(v) {
  return Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z);
}

function clamp(v, lo, hi) {
  return v < lo ? lo : (v > hi ? hi : v);
}

/**
 * Base ortonormal em torno de uma direcao, para montar o cone de dispersao e o
 * desvio da penetracao. Escreve em _right/_up.
 */
function buildBasis(d) {
  const upRef = Math.abs(d.y) > 0.9995 ? _AXIS_X : _WORLD_UP;
  _right.crossVectors(upRef, d);
  const len = _right.length();
  if (len < 1e-6) {
    _right.crossVectors(_AXIS_X, d);
    const l2 = _right.length();
    if (l2 < 1e-6) { _right.set(1, 0, 0); } else { _right.multiplyScalar(1 / l2); }
  } else {
    _right.multiplyScalar(1 / len);
  }
  _up.crossVectors(d, _right).normalize();
}

/** Gira `out` em torno de `d` por (raio angular, azimute), usando _right/_up. */
function coneDirection(out, d, angle, azimuth) {
  const sa = Math.sin(angle);
  out.copy(d).multiplyScalar(Math.cos(angle));
  out.addScaledVector(_right, Math.cos(azimuth) * sa);
  out.addScaledVector(_up, Math.sin(azimuth) * sa);
  const l = out.length();
  if (l > 1e-8) out.multiplyScalar(1 / l); else out.copy(d);
}

export class Ballistics {
  constructor(ctx) {
    this.ctx = ctx;

    // Estado do tiro corrente (evita passar 8 argumentos entre metodos).
    this._sTeam = TEAM.PLAYER;
    this._sShooter = null;
    this._sWeapon = null;
    this._sRange = DEFAULT_RANGE;
    this._sDamage = DEFAULT_DAMAGE;
    this._sFromPlayer = true;

    // Curva de queda de dano resolvida uma vez por tiro.
    this._foFn = null;
    this._foStart = DEFAULT_RANGE * FALLOFF_START_FRACTION;
    this._foEnd = DEFAULT_RANGE * FALLOFF_END_FRACTION;
    this._foMin = FALLOFF_MIN;

    // Dados do acerto de alvo copiados do resultado pooled do TargetRegistry.
    this._tEntity = null;
    this._tPart = 'chest';
    this._tMultiplier = 1;
    this._tDistance = 0;

    // Dados do acerto de cenario copiados do resultado do CollisionWorld.
    this._cObject = null;
    this._cSurface = 'concrete';
    this._cDistance = 0;

    this.shotsFired = 0;
    this.pelletsFired = 0;
    this.hits = 0;
    this.kills = 0;

    this._warned = null;
  }

  async init() { /* sem construcao pesada: a balistica so precisa do ctx */ }

  // --- API principal -------------------------------------------------------

  /**
   * Dispara um tiro (1 projetil, ou N chumbos para espingarda).
   *
   * @param {{origin:THREE.Vector3, dir:THREE.Vector3, weapon?:object, shooter?:object,
   *          team?:number, spread?:number, damage?:number, range?:number, pellets?:number}} params
   * @returns {{pellets:number, hits:number, kills:number, damage:number, penetrations:number}}
   *          objeto REAPROVEITADO - leia imediatamente.
   */
  fire(params) {
    const out = _shotResult;
    out.pellets = 0; out.hits = 0; out.kills = 0; out.damage = 0; out.penetrations = 0;
    if (!params) return out;

    const origin = params.origin;
    const dir = params.dir;
    if (!origin || !dir) return out;

    _origin.set(origin.x, origin.y, origin.z);
    _dir.set(dir.x, dir.y, dir.z);
    if (!finite(_origin) || !finite(_dir)) return out;
    const dirLen = _dir.length();
    if (dirLen < 1e-8) return out;
    _dir.multiplyScalar(1 / dirLen);

    const weapon = params.weapon || null;
    const shooter = params.shooter !== undefined ? params.shooter : null;

    let team = params.team;
    if (typeof team !== 'number') {
      team = (shooter && typeof shooter.team === 'number') ? shooter.team : TEAM.PLAYER;
    }

    const range = clamp(this._num(params.range, this._num(weapon && weapon.range, DEFAULT_RANGE)), 1, 400);
    const damage = clamp(this._num(params.damage, this._num(weapon && weapon.damage, DEFAULT_DAMAGE)), 0, 1000);
    let pellets = Math.floor(this._num(params.pellets, this._num(weapon && weapon.pellets, 1)));
    if (!Number.isFinite(pellets) || pellets < 1) pellets = 1;
    if (pellets > MAX_PELLETS) pellets = MAX_PELLETS;

    const spread = this._normalizeSpread(this._num(params.spread, this._num(weapon && weapon.spread, 0)));

    this._sTeam = team;
    this._sShooter = shooter;
    this._sWeapon = weapon;
    this._sRange = range;
    this._sDamage = damage;
    this._sFromPlayer = this._isPlayerShooter(shooter, team);
    this._resolveFalloff(weapon, damage, range);

    // Origem visual do tracer: a boca da arma na tela, nao o olho da camera.
    this._resolveMuzzle(this._sFromPlayer);

    buildBasis(_dir);

    for (let p = 0; p < pellets; p++) {
      this._pelletDirection(spread, p, pellets);
      this._tracePellet(p, out);
    }

    this.shotsFired++;
    this.pelletsFired += pellets;
    this.hits += out.hits;
    this.kills += out.kills;
    out.pellets = pellets;
    return out;
  }

  // --- resolucao de um projetil -------------------------------------------

  /**
   * Percorre o caminho de um projetil, atravessando ate MAX_PENETRATIONS
   * superficies. Para no primeiro alvo atingido (corpo nao e penetravel aqui).
   */
  _tracePellet(pelletIndex, out) {
    const team = this._sTeam;
    const range = this._sRange;

    _curOrigin.copy(_origin);
    _curDir.copy(_pelletDir);
    _endPoint.copy(_origin).addScaledVector(_curDir, range);

    let traveled = 0;
    let damageScale = 1;
    let penetrations = 0;
    let lastObject = null;
    let guard = 0;

    // guard: teto duro de iteracoes. Nada pode prender o laco de frame.
    while (guard++ < 8) {
      const remaining = range - traveled;
      if (remaining <= 0.02) break;

      // --- 1. alvos (bots, jogador) ---
      let targetDist = Infinity;
      const tHit = this._castTargets(_curOrigin, _curDir, remaining, team);
      if (tHit && tHit.entity && tHit.point) {
        _tPoint.set(tHit.point.x, tHit.point.y, tHit.point.z);
        if (tHit.normal) _tNormal.set(tHit.normal.x, tHit.normal.y, tHit.normal.z);
        else _tNormal.copy(_curDir).multiplyScalar(-1);
        if (!finite(_tNormal) || _tNormal.lengthSq() < 1e-8) _tNormal.copy(_curDir).multiplyScalar(-1);
        this._tEntity = tHit.entity;
        this._tPart = HITBOX_MULTIPLIER[tHit.part] !== undefined ? tHit.part : 'chest';
        this._tMultiplier = Number.isFinite(tHit.multiplier)
          ? tHit.multiplier
          : (HITBOX_MULTIPLIER[this._tPart] || 1);
        targetDist = Number.isFinite(tHit.distance) ? tHit.distance : _tPoint.distanceTo(_curOrigin);
        this._tDistance = targetDist;
      }

      // --- 2. cenario ---
      let sceneDist = Infinity;
      const cHit = this._castScene(_curOrigin, _curDir, remaining);
      if (cHit && cHit.point) {
        _cPoint.set(cHit.point.x, cHit.point.y, cHit.point.z);
        if (cHit.normal) _cNormal.set(cHit.normal.x, cHit.normal.y, cHit.normal.z);
        else _cNormal.copy(_curDir).multiplyScalar(-1);
        if (!finite(_cNormal) || _cNormal.lengthSq() < 1e-8) _cNormal.copy(_curDir).multiplyScalar(-1);
        else _cNormal.normalize();
        this._cObject = cHit.object || null;
        this._cSurface = this._surfaceOf(cHit);
        sceneDist = Number.isFinite(cHit.distance) ? cHit.distance : _cPoint.distanceTo(_curOrigin);
        this._cDistance = sceneDist;
        if (!finite(_cPoint)) sceneDist = Infinity;
      }

      // --- 3. o mais proximo vence (empate favorece o alvo) ---
      if (targetDist <= sceneDist + HIT_EPS && targetDist < Infinity) {
        const total = traveled + targetDist;
        this._hitEntity(total, damageScale, out);
        _endPoint.copy(_tPoint);
        break;
      }

      if (sceneDist === Infinity) {
        // Bala perdida: nada no caminho ate o alcance maximo.
        _endPoint.copy(_curOrigin).addScaledVector(_curDir, remaining);
        break;
      }

      traveled += sceneDist;
      _endPoint.copy(_cPoint);

      const surface = this._cSurface;
      const pen = PENETRABLE[surface] || null;

      // Face de SAIDA da mesma parede (quando a colisao reporta faces internas):
      // e o mesmo obstaculo que ja foi pago, nao cobra penetracao de novo.
      const isExitFace = lastObject !== null && lastObject === this._cObject && sceneDist < WALL_MAX_THICKNESS;

      // O furo aparece na ENTRADA e tambem na SAIDA da parede atravessada.
      const willPass = isExitFace || (pen !== null && penetrations < MAX_PENETRATIONS);
      this._emitImpact(traveled, willPass);

      if (isExitFace) {
        _curOrigin.copy(_cPoint).addScaledVector(_curDir, PENETRATION_STEP);
        traveled += PENETRATION_STEP;
        continue;
      }

      if (!pen || penetrations >= MAX_PENETRATIONS) break;   // concreto, tijolo, metal grosso

      penetrations++;
      out.penetrations++;
      damageScale *= pen.damage;
      lastObject = this._cObject;

      // Desvio pequeno: a bala sai da parede um pouco fora do eixo original.
      if (pen.deviation > 0) {
        buildBasis(_curDir);
        const a = pen.deviation * DEG2RAD * Math.sqrt(Math.random());
        coneDirection(_curDir, _curDir, a, Math.random() * TAU);
      }
      _curOrigin.copy(_cPoint).addScaledVector(_curDir, PENETRATION_STEP);
      traveled += PENETRATION_STEP;
    }

    // Tracer: apenas no primeiro projetil. Espingarda com 8 rastros luminosos
    // parece fogo de artificio; o FX ainda decide se este tiro exibe rastro.
    if (pelletIndex === 0) this._emitTracer();
  }

  // --- acerto em entidade --------------------------------------------------

  _hitEntity(distance, damageScale, out) {
    const ctx = this.ctx;
    const entity = this._tEntity;
    if (!entity) return;

    const part = this._tPart;
    const headshot = part === 'head';
    const multiplier = this._tMultiplier;
    const falloff = this._falloffAt(distance);
    const amount = this._sDamage * multiplier * falloff * damageScale;

    // Info entregue ao onDamage do registro (preenchida ANTES da chamada).
    _dmgInfo.amount = amount;
    _dmgInfo.part = part;
    _dmgInfo.point.copy(_tPoint);
    _dmgInfo.normal.copy(_tNormal);
    _dmgInfo.direction.copy(_curDir);
    _dmgInfo.from.copy(_origin);
    _dmgInfo.source = this._sShooter;
    _dmgInfo.weapon = this._sWeapon;
    _dmgInfo.team = this._sTeam;
    _dmgInfo.distance = distance;
    _dmgInfo.headshot = headshot;

    const killed = this._applyDamage(entity, amount, part);

    // Sangue e feedback saem no MESMO frame do tiro - nada de fila.
    const fx = ctx.fx;
    if (fx && typeof fx.blood === 'function') {
      _fxBloodArgs.point.copy(_tPoint);
      _fxBloodArgs.normal.copy(_tNormal);
      _fxBloodArgs.direction.copy(_curDir);
      try { fx.blood(_fxBloodArgs); } catch (err) { this._warnOnce('fx.blood', err); }
    }

    _evDamaged.entity = entity;
    _evDamaged.amount = amount;
    _evDamaged.part = part;
    _evDamaged.point.copy(_tPoint);
    _evDamaged.normal.copy(_tNormal);
    _evDamaged.source = this._sShooter;
    _evDamaged.weapon = this._sWeapon;
    _evDamaged.headshot = headshot;
    _evDamaged.distance = distance;
    _evDamaged.killed = killed;
    ctx.events.emit(EVENTS.ENTITY_DAMAGED, _evDamaged);

    if (killed) {
      _evKilled.entity = entity;
      _evKilled.source = this._sShooter;
      _evKilled.weapon = this._sWeapon;
      _evKilled.headshot = headshot;
      _evKilled.part = part;
      ctx.events.emit(EVENTS.ENTITY_KILLED, _evKilled);
    }

    if (this._sFromPlayer) {
      _evHitmarker.headshot = headshot;
      _evHitmarker.killed = killed;
      ctx.events.emit(EVENTS.HITMARKER, _evHitmarker);
    }

    out.hits++;
    out.damage += amount;
    if (killed) out.kills++;
  }

  /**
   * Aplica o dano pelo canal do registro (onDamage) e, se ele nao existir ou
   * comprovadamente nao tirou vida, pelo metodo damage() da propria entidade.
   * A morte e detectada lendo health/alive antes e depois - nao depende do que
   * a entidade devolve, que nao esta no contrato.
   */
  _applyDamage(entity, amount, part) {
    const registry = this.ctx.targets;
    const entry = (registry && typeof registry.get === 'function') ? registry.get(entity) : null;

    const hadHealth = typeof entity.health === 'number';
    const healthBefore = hadHealth ? entity.health : 0;
    const aliveBefore = entity.alive !== false;

    let handled = false;
    if (entry && typeof entry.onDamage === 'function') {
      try { entry.onDamage(amount, _dmgInfo); handled = true; }
      catch (err) { this._warnOnce('targets.onDamage', err); }
    }

    // Fallback so quando da para PROVAR que a vida nao mudou (evita dano dobrado
    // em entidades que guardam a vida em outro campo).
    const unchanged = hadHealth && entity.health === healthBefore && entity.alive !== false;
    if ((!handled || unchanged) && typeof entity.damage === 'function') {
      try { entity.damage(amount, _dmgInfo.from, part); }
      catch (err) { this._warnOnce('entity.damage', err); }
    }

    const deadNow = entity.alive === false || (typeof entity.health === 'number' && entity.health <= 0);
    return aliveBefore && deadNow;
  }

  // --- acerto no cenario ---------------------------------------------------

  _emitImpact(distance, penetrated) {
    const ctx = this.ctx;

    _evImpact.point.copy(_cPoint);
    _evImpact.normal.copy(_cNormal);
    _evImpact.surface = this._cSurface;
    _evImpact.object = this._cObject;
    _evImpact.fromPlayer = this._sFromPlayer;
    _evImpact.weapon = this._sWeapon;
    _evImpact.distance = distance;
    _evImpact.penetrated = !!penetrated;
    ctx.events.emit(EVENTS.BULLET_IMPACT, _evImpact);

    const fx = ctx.fx;
    if (fx && typeof fx.impact === 'function') {
      _fxImpactArgs.point.copy(_cPoint);
      _fxImpactArgs.normal.copy(_cNormal);
      _fxImpactArgs.surface = this._cSurface;
      _fxImpactArgs.scale = 1;
      try { fx.impact(_fxImpactArgs); } catch (err) { this._warnOnce('fx.impact', err); }
    }
  }

  _emitTracer() {
    const fx = this.ctx.fx;
    if (!fx || typeof fx.tracer !== 'function') return;
    _fxTracerArgs.from.copy(_tracerFrom);
    _fxTracerArgs.to.copy(_endPoint);
    const w = this._sWeapon;
    const speed = this._num(w && w.tracerSpeed, this._num(w && w.muzzleVelocity, 340));
    _fxTracerArgs.speed = clamp(speed, 60, 1200);
    _fxTracerArgs.weapon = w;
    _fxTracerArgs.fromPlayer = this._sFromPlayer;
    try { fx.tracer(_fxTracerArgs); } catch (err) { this._warnOnce('fx.tracer', err); }
  }

  // --- consultas protegidas ------------------------------------------------

  _castTargets(origin, dir, maxDist, team) {
    const t = this.ctx.targets;
    if (!t || typeof t.raycast !== 'function') return null;
    try { return t.raycast(origin, dir, maxDist, team) || null; }
    catch (err) { this._warnOnce('targets.raycast', err); return null; }
  }

  _castScene(origin, dir, maxDist) {
    const c = this.ctx.collision;
    if (!c || typeof c.raycast !== 'function') return null;
    try { return c.raycast(origin, dir, maxDist) || null; }
    catch (err) { this._warnOnce('collision.raycast', err); return null; }
  }

  /** Superficie do acerto: o que a colisao informou, senao o userData do mesh. */
  _surfaceOf(hit) {
    if (hit && typeof hit.surface === 'string' && hit.surface.length > 0) return hit.surface;
    const obj = hit && hit.object;
    const fromData = obj && obj.userData ? obj.userData.surface : null;
    if (typeof fromData === 'string' && fromData.length > 0) return fromData;
    const mats = this.ctx.materials;
    if (obj && mats && typeof mats.surfaceOf === 'function') {
      try {
        const s = mats.surfaceOf(obj);
        if (typeof s === 'string' && s.length > 0) return s;
      } catch (err) { this._warnOnce('materials.surfaceOf', err); }
    }
    return 'concrete';
  }

  // --- dispersao -----------------------------------------------------------

  /**
   * Spread pode chegar em radianos (0.02) ou em graus (1.2). Acima de 0.35 rad
   * (20 graus de meia-abertura) nao existe arma real, entao esse valor so pode
   * ser grau. Heuristica segura nas duas convencoes.
   */
  _normalizeSpread(value) {
    let s = Number.isFinite(value) ? Math.abs(value) : 0;
    if (s > 0.35) s *= DEG2RAD;
    return clamp(s, 0, 0.7);
  }

  _pelletDirection(spread, index, pellets) {
    if (spread <= 1e-5) { _pelletDir.copy(_dir); return; }

    let radius;
    let azimuth;
    if (pellets > 1) {
      // Padrao estratificado: cada chumbo ocupa um anel proprio, com jitter.
      // Da um cartucho "cheio" e reconhecivel, sem os buracos do sorteio puro.
      const frac = (index + 0.35 + Math.random() * 0.55) / pellets;
      radius = spread * Math.pow(clamp(frac, 0, 1), 0.62);
      azimuth = index * GOLDEN_ANGLE + Math.random() * 0.9;
    } else {
      radius = spread * Math.pow(Math.random(), 0.7);
      azimuth = Math.random() * TAU;
    }
    coneDirection(_pelletDir, _dir, radius, azimuth);
  }

  // --- queda de dano -------------------------------------------------------

  /**
   * Le a queda de dano da arma tolerando os formatos plausiveis (funcao, objeto
   * com inicio/fim/minimo, ou numero) e cai numa curva padrao quando nao houver.
   */
  _resolveFalloff(weapon, baseDamage, range) {
    this._foFn = null;
    this._foStart = range * FALLOFF_START_FRACTION;
    this._foEnd = range * FALLOFF_END_FRACTION;
    this._foMin = FALLOFF_MIN;

    const f = weapon ? (weapon.damageFalloff !== undefined ? weapon.damageFalloff : weapon.falloff) : undefined;
    if (f === undefined || f === null) return;

    if (typeof f === 'function') { this._foFn = f; return; }

    if (typeof f === 'number') {
      if (f > 0 && f <= 1) this._foMin = f;                 // multiplicador no fim
      else if (f > 1) { this._foStart = f; this._foEnd = Math.max(f + 1, range * FALLOFF_END_FRACTION); }
      return;
    }

    if (typeof f === 'object') {
      const start = this._num(f.start, this._num(f.startDistance, this._num(f.near, this._num(f.begin, this._foStart))));
      const end = this._num(f.end, this._num(f.endDistance, this._num(f.far, this._foEnd)));
      let min = this._num(f.min, this._num(f.minMultiplier, this._num(f.minScale, this._num(f.multiplier, this._num(f.minDamage, this._foMin)))));
      // Veio como dano absoluto no fim da curva? converte para multiplicador.
      if (min > 1) min = baseDamage > 0 ? min / baseDamage : FALLOFF_MIN;
      this._foStart = clamp(start, 0, 400);
      this._foEnd = clamp(Math.max(end, this._foStart + 0.5), 0.5, 500);
      this._foMin = clamp(min, 0.05, 1);
    }
  }

  _falloffAt(distance) {
    if (this._foFn) {
      try {
        const m = this._foFn(distance);
        if (Number.isFinite(m)) return clamp(m, 0.05, 4);
      } catch (err) {
        this._warnOnce('weapon.damageFalloff', err);
      }
      this._foFn = null;
    }
    if (distance <= this._foStart) return 1;
    if (distance >= this._foEnd) return this._foMin;
    const t = (distance - this._foStart) / (this._foEnd - this._foStart);
    const s = t * t * (3 - 2 * t);   // smoothstep: queda sem degrau perceptivel
    return 1 + (this._foMin - 1) * s;
  }

  // --- auxiliares ----------------------------------------------------------

  _num(value, fallback) {
    return Number.isFinite(value) ? value : fallback;
  }

  /**
   * Time do jogador ja separa quase tudo (bots sao sempre ENEMY); a checagem de
   * identidade cobre o caso de um bot mal configurado no time do jogador.
   */
  _isPlayerShooter(shooter, team) {
    if (shooter && (shooter.isBot === true || shooter.isEnemy === true)) return false;
    if (shooter && this.ctx.player && shooter === this.ctx.player) return true;
    if (shooter && this.ctx.controller && shooter === this.ctx.controller) return true;
    return team === TEAM.PLAYER;
  }

  /**
   * Ponto de saida visual do tracer. O olho da camera esta atras da arma; sair
   * da boca do cano e o que faz o rastro parecer preso ao modelo. So aceita o
   * ponto se ele estiver perto do jogador (viewmodel ainda nao pronto devolve
   * lixo, e um tracer nascendo na origem do mundo e um bug visivel de longe).
   */
  _resolveMuzzle(fromPlayer) {
    _tracerFrom.copy(_origin);
    if (!fromPlayer) return;
    const vm = this.ctx.viewModel;
    if (!vm || typeof vm.getMuzzleWorldPosition !== 'function') return;
    _muzzleTmp.set(NaN, NaN, NaN);
    try {
      const r = vm.getMuzzleWorldPosition(_muzzleTmp);
      const p = (r && Number.isFinite(r.x)) ? r : _muzzleTmp;
      if (!Number.isFinite(p.x) || !Number.isFinite(p.y) || !Number.isFinite(p.z)) return;
      const dx = p.x - _origin.x;
      const dy = p.y - _origin.y;
      const dz = p.z - _origin.z;
      if (dx * dx + dy * dy + dz * dz < 9) _tracerFrom.set(p.x, p.y, p.z);
    } catch (err) {
      this._warnOnce('viewModel.getMuzzleWorldPosition', err);
    }
  }

  /** Um sistema que falha degrada em silencio: avisa uma vez e segue jogando. */
  _warnOnce(key, err) {
    if (!this._warned) this._warned = new Set();
    if (this._warned.has(key)) return;
    this._warned.add(key);
    console.warn(`[Ballistics] ${key} falhou (avisado uma vez):`, err);
  }

  dispose() {
    this._tEntity = null;
    this._cObject = null;
    this._sShooter = null;
    this._sWeapon = null;
  }
}
